import { CustomDataTableContainer } from "./CustomDataTableContainer";
import CustomPagination from "../CustomPagination/CustomPagination";
import { Checkbox, CircularProgress, SelectChangeEvent } from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { IoFilterSharp } from "react-icons/io5";
import { TbArrowsSort } from "react-icons/tb";
import SearchFilter from "../SearchFilter/SearchFilter";
import { ExtendedMemberType } from "../../../hooks/useContactRelatedApi";
import { CheckedValue, Pagination } from "../../../pages/Contact/MainDataTable";
import { VariableSizeList as List } from "react-window";
import useWindowWidth from "./useWindowWidth";

export interface Column<T = any> {
  headerName: string;
  field: string;
  width?: number;
  filtering?: boolean;
  displayFields?: boolean;
  class?: string;
  renderCell?: (params: { row: T }) => React.ReactNode;
  defaultChecked?: boolean;
  exportPdfName?: (params: any) => string;
  isExisted?: (params: any) => string;
  sorted?: boolean;
  align?: string;
}

interface Row {
  contactId?: number;
  companyDriveId?: number;
  branchId?: number;
  isBranch?: number;
  [key: string]: any;
}

interface CustomDataTableProps<T = any> {
  rows: Row[];
  columns: Column[];
  paginationInfo: Pagination;
  handlePageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  pageOption: string;
  handleChangePageOption: (
    event: SelectChangeEvent<number>,
    child: React.ReactNode
  ) => void;
  loading: boolean;
  handleRowCheckboxChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) => void;
  handleSelectAllChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectAll?: boolean;
  selectedRows?: number[];
  FixedTable?: boolean;
  isCheckboxShow?: boolean;
  isBranchFieldExist?: boolean;
  defaultChecker?: (column: Column) => boolean;
  customTableHeight?: string;
  linkField?: string;
  linkFieldNav?: (row: T,column?: string) => string;
  dynamicRowHeight?: number;
  checkedValue?: CheckedValue;
  setCheckedValue?: React.Dispatch<React.SetStateAction<CheckedValue>>;
  handleApplyFilterBtn?: (callFilterselection?: boolean) => void;
  toggleFilter?: (column: Column) => void;
  openFilter?: string | null;
  setOpenFilter?: React.Dispatch<React.SetStateAction<string | null>>;
  handleInputFocus?: (field: string) => void;
  branchLocationList?: Array<{ id: number; name: string; userFrontId: number }>;
  membershipList?: Array<ExtendedMemberType>;
  filterData?: any;
  setFilterData?: React.Dispatch<React.SetStateAction<any[]>>;
  handleSearch?: (e: React.ChangeEvent<HTMLInputElement>, type: string) => void;
  contactFilteredIds?: string[];
  setContactFilteredIds?: React.Dispatch<React.SetStateAction<string[]>>;
  filteringData?: any;
  setSearchInputValue?: React.Dispatch<React.SetStateAction<string>>;
  searchInputValue?: string;
  toggleSorting?: (column: Column) => void;
}

const CustomDataTable: React.FC<CustomDataTableProps> = ({
  rows,
  columns,
  paginationInfo,
  handlePageChange,
  pageOption,
  handleChangePageOption,
  loading,
  handleRowCheckboxChange,
  handleSelectAllChange,
  selectAll,
  selectedRows = [],
  FixedTable = false,
  isCheckboxShow = true,
  isBranchFieldExist = true,
  defaultChecker = () => true,
  dynamicRowHeight = 30,
  customTableHeight = "",
  linkField = "",
  linkFieldNav,
  checkedValue = {},
  setCheckedValue = () => {},
  handleApplyFilterBtn = () => {},
  toggleFilter,
  openFilter,
  setOpenFilter = () => {},
  handleInputFocus,
  branchLocationList = [],
  membershipList = [],
  filterData,
  setFilterData = () => {},
  handleSearch = () => {},
  contactFilteredIds = [],
  setContactFilteredIds = () => {},
  filteringData,
  setSearchInputValue = () => {},
  searchInputValue = "",
  toggleSorting,
}) => {
  const sessionScope = useSelector(
    (state: any) => state.sessionData.sessionScope
  );
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const sessionBranchId = sessionScope?.branchId;
  const checkBoxForSelection = (row: Row) => {
    if (row.branchId) {
      return (
        (isBranchFieldExist || (row.isBranch === 0 && row.isDefault ===0)) &&
        row.branchId == sessionBranchId
      );
    } else {
      return true;
    }
  };
  const RowRenderer = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const row = rows[index] as Row;
    const rowHeight = getItemSize(); // Get the dynamic row height
    const dynamicPadding = (rowHeight - 40) / 2; // Calculate dynamic padding (24 is the checkbox height)

    return (
      <tr  style={{ ...style, display: "flex", width: "100%" , height: dynamicRowHeight  }} key={index}>
        {isCheckboxShow && (
          <td
            className="contactTdRow checkboxTd"
            style={{
              padding: `${dynamicPadding}px 5px`, // Apply dynamic padding
              flex: "0 0 auto",
              alignItems: "center",
              display: "flex !important",
              width: "50px",
            }}
          >
            {checkBoxForSelection(row) && (
              <Checkbox
                className="checkboxkp"
                onChange={(event) => {
                  event.stopPropagation();
                  handleRowCheckboxChange &&
                    handleRowCheckboxChange(
                      event,
                      row.contactId ?? row.companyDriveId ?? -1
                    );
                }}
                checked={selectedRows.includes(
                  row.contactId ?? row.companyDriveId ?? 0
                )}
                style={{ overflow: "hidden" }} // Disable scrollbar
              />
            )}
          </td>
        )}
        {columns?.map((column) => (
          <td
            className={column?.class ?? "" + " " + "contactTdRow"} 
            style={{
              cursor:'pointer',
              padding: column.field === "name" ? "0" : "4px 8px",
              alignItems: "center",
              display: defaultChecker && defaultChecker(column) ? "flex" : "none",
              color: column.field === "name" ? "#00a4e5" : "",
              fontWeight: column.field === "name" ? 600 : 0,
              // cursor: column.field === "name" ? "pointer" : "",
              textAlign: column.field === "active" ? "center" : undefined,
              flex: `0 0 ${((column.width ?? 100) + extraWidthPerColumn)}px`,
              wordBreak: column.field === "srNo" ? "keep-all" : "break-word", // Break long words
              whiteSpace: "normal", // Allow text to wrap
              overflowWrap: "break-word", // Ensure wrapping for long words

              width: `${(column.width ?? 100) + extraWidthPerColumn}px`,
    minWidth: `${(column.width ?? 100) + extraWidthPerColumn}px`,
    maxWidth: `${(column.width ?? 100) + extraWidthPerColumn}px`,
            }}
            key={column.field}
            {...(column.field === "active" || column.field === "action"
              ? {}
              : {
                  title: column.renderCell
                    ? (column.renderCell({ row })?.toString() || "")
                    : (row[column.field]?.toString() || "")
                }
            )}
          >

              {(() => {
                if (column.field === "active" || column.field === "action") {
                  return column.renderCell ? column.renderCell({ row }) : null;
                }
                const cellValue = row[column.field];
                
                let tooltipText = "";
                if (typeof cellValue === "string" || typeof cellValue === "number") {
                  if (column.field ==='rating' && typeof cellValue === "number") {
                    if(cellValue.toString()=='1'){
                    		tooltipText="1 | TERRIBLE"
                    	}
                    	if(cellValue.toString()=='2'){
                    		tooltipText="2 | Bad"
                    	}
                    	if(cellValue.toString()=='3'){
                    		tooltipText="3 | OKAY"
                    	}
                    	if(cellValue.toString()=='4'){
                    		tooltipText="4 | GOOD"
                    	}
                    	if(cellValue.toString()=='5'){
                    		tooltipText="5 | GREAT"
                    	}
                    console.log("column.field", column.field , cellValue.toString()); 
                  } else {

                    tooltipText = cellValue.toString();
                  }
                } else if (typeof cellValue === "object" && cellValue !== null) {
                  tooltipText = JSON.stringify(cellValue);
                }
              
                const content = column.renderCell
                  ? column.renderCell({ row }) // ✅ Preserve original JSX
                  : (() => {
                      const text = tooltipText;
                      if (column.field === "addressLine1" || column.field === "addressLine2") {
                  

                        return text.length > 50 ? text.slice(0, 50) + "..." : text;
                      }
                     
                     
                      return text.length > 30 ? text.slice(0, 30) + "..." : text;
                    })();
              
                    
                
                return column.field === linkField || linkField.includes(column.field) ? (
                  <a
                    className="name_link"
                    href={linkFieldNav ? linkFieldNav(row,column.field) : "#"}
                    title={tooltipText}
                    style={{
                      display: 'inline-block',
                      maxWidth: '100%',
                      width: 'fit-content',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {content}
                  </a>
                ) : (
                  <span
                    title={tooltipText}
                    style={{
                      display: column.field === "name" ? "flex" : "inline-block", 
                      maxWidth: '100%',
                      width : '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      padding: column.field === "name" ? "0 5px" : "",
                      height: column.field === "name" ? "100%" : "",
                      alignItems : 'center',

                    }}
                  >
                    {content}
                  </span>
                );
              })()}
            
            {/* {column.field === linkField ? (
              <a
                className="name_link"
                href={linkFieldNav ? linkFieldNav(row) : "#"}
              >
                {column.renderCell ? column.renderCell({ row }) : null}
              </a>
            ) : column.renderCell ? (
              column.renderCell({ row })
            ) : (
              row[column.field]
            )} */}
          </td>
        ))}
      </tr>
    );
  };

  // const calculateRowHeight = (row: Row): number => {
  //   // Example logic to calculate height based on content
  //   const baseHeight = 28; // Minimum height
  //   const extraHeightPerLine = 30; // Additional height per line of content
  //   const content = Object.values(row).join(" "); // Combine all row values as a string
  //   const lineCount = Math.ceil(content.length / 25); // Assume 30 characters per line
  //   return baseHeight + (lineCount - 1) + (FixedTable ? 10 : extraHeightPerLine);
  // };

  // const rowHeights = rows.map(calculateRowHeight);

  const getItemSize = (): number => dynamicRowHeight //rowHeights[index] ?? 50; // Default to 50 if undefined

  // Calculate the total width of all visible columns
  let visibleColumnCount = FixedTable ? 0 : 0;

  const totalTableWidth = columns.reduce((total, column) => {
    if (defaultChecker && defaultChecker(column)) {
      visibleColumnCount++;
      return total + (column.width || 100);
    }
    return total;
  }, 0);

  let extraWidthPerColumn = 0;
  const viewportWidth = useWindowWidth();
  let columnRequired = FixedTable ? Math.max(1200, viewportWidth - 100) : Math.max(1000, viewportWidth - 150);
  // let columnRequired = FixedTable ? 1412 : 1382;
  if (totalTableWidth < columnRequired && visibleColumnCount > 0) {
    extraWidthPerColumn = (columnRequired - totalTableWidth) / visibleColumnCount;
  }
  return (
    <>
      <CustomDataTableContainer>
        <div
          style={{
            height: customTableHeight,
            overflowX: "auto",
            overflowY: "hidden",
            position: "relative",
            willChange: "transform",
            direction: "ltr",
            boxSizing: "border-box",
            fontSize: "13px",
            fontWeight: "200",
            fontFamily: '"Plus Jakarta Sans", sans-serif',
          }}
          className="custom-table-container"
        >
          <div style={{ minWidth: columnRequired }}>
            <table
              id="my-table"
              className="custom-table"
              style={{ tableLayout: "fixed", width: columnRequired }}
            >
              <thead>
              <tr style={{ height: FixedTable ? "35px" : undefined }}>
                  {isCheckboxShow && (
                    <th style={{ width: "50px" }}>
                      <Checkbox
                        onChange={handleSelectAllChange}
                        checked={selectAll}
                      />
                    </th>
                  )}
                  {columns?.map((column, index) => (
                    <React.Fragment key={index}>
                      <th
                        className="common__theader_name"
                        style={{
                          display: defaultChecker && defaultChecker(column) ? "" : "none",
                          width: (column.width ?? 0) + extraWidthPerColumn,
                        }}
                      >
                        <div className="d-flex justify-content-between">
                          <div>{column.headerName}</div>
                          {column.sorted && (
                            <div
                              onClick={() =>
                                toggleSorting && toggleSorting(column)
                              }
                              style={{
                                cursor: "pointer",
                                color: checkedValue?.hasOwnProperty(
                                  column.field
                                )
                                  ? "rgb(0 154 231)"
                                  : "",
                              }}
                            >
                              <TbArrowsSort />
                            </div>
                          )}
                          {column.filtering && (
                            <div
                              onClick={() =>
                                toggleFilter && toggleFilter(column)
                              }
                              style={{
                                cursor: "pointer",
                                color:
                                  checkedValue?.hasOwnProperty(column.field) &&
                                  Object.keys(
                                    checkedValue[column.field] || {}
                                  ).length > 0
                                    ? "blue"
                                    : "inherit",
                              }}
                            >
                              <IoFilterSharp />
                            </div>
                          )}
                        </div>
                      </th>
                    </React.Fragment>
                  ))}
                  {columns?.map(
                    (column, index) =>
                      column?.displayFields && (
                        <React.Fragment key={index}>
                          <th
                            className="common__theader_name"
                            style={{
                              display: defaultChecker &&defaultChecker(column) ? "" : "none",
                              width: column.width,
                            }}
                          >
                            <div className="d-flex justify-content-between">
                              <div>{column.headerName}</div>
                              {column.filtering && (
                                <div
                                  onClick={() =>
                                    toggleFilter && toggleFilter(column)
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <IoFilterSharp />
                                </div>
                              )}
                            </div>
                          </th>
                        </React.Fragment>
                      )
                  )}
                </tr>
              </thead>
              <tbody>
                {openFilter && (
                  <tr
                  className="filterRow"
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "white",
                      height: "30px",
                      zIndex: 1000, // Increased zIndex to ensure it appears above rows
                    }}
                  >
                    <td
                      className="filter-design"
                      
                      style={{ borderRight: 0 ,  verticalAlign: "middle", }}
                    ></td>
                    {columns
                      ?.filter((col) => col.defaultChecked)
                      ?.map((column, index) => (
                        <React.Fragment key={index}>
                          {column.field === openFilter ? (
                            <SearchFilter
                              index={index}
                              isOpen={column.field === openFilter}
                              column={openFilter}
                              setOpenFilter={setOpenFilter}
                              onFocus={() =>
                                handleInputFocus &&
                                handleInputFocus(column.field)
                              }
                              setCheckedValue={setCheckedValue}
                              checkedValue={checkedValue}
                              handleApplyFilterBtn={handleApplyFilterBtn}
                              branchLocationList={branchLocationList}
                              membershipList={membershipList}
                              filteringData={filteringData}
                              filterData={filterData}
                              setFilterData={setFilterData}
                              handleSearch={handleSearch}
                              contactFilteredIds={contactFilteredIds}
                              setContactFilteredIds={setContactFilteredIds}
                              setSearchInputValue={setSearchInputValue}
                              searchInputValue={searchInputValue}
                            />
                          ) : (
                            // Render same outer div to preserve layout and alignment
                            <td key={index} className={`filter-design ${index}`} style={{ borderRight: 0 }} />
                          )}
                        </React.Fragment>
                      ))}
                  </tr>
                )}
                {loading ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{
                        textAlign: "center",
                        backgroundColor: "white",
                        border: "none",
                      }}
                    >
                      <CircularProgress />
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{
                        textAlign: "center",
                        backgroundColor: "white",
                        border: "none",
                      }}
                    >
                      No records found
                    </td>
                  </tr>
                ) : (
                  <tr className={isSafari ? `custom-table-row ${FixedTable ? "custom-table-row-safari" : ""}`: ""}style={isSafari && openFilter ? { top: "94px" } : {}}>
                    {/* {" "} */}
                    <td
                      colSpan={columns.length + 2}
                      style={{ padding: 0, height: "100%" }}
                    >
                      <div
                       className={`kp ${isSafari && FixedTable ? "mainlistContainer" : ""}`}
                        style={{
                          position: "relative",
                          height: "calc(100% - 100px)", // Adjust height to ensure last records are not cut off
                          width: FixedTable
                            ? "100%"
                            : totalTableWidth < columnRequired &&
                              visibleColumnCount > 0
                            ? columnRequired + 50
                            : `${totalTableWidth + 50}px`,
                          overflow: "auto",
                          willChange: "transform",
                          direction: "ltr",
                          boxSizing: "border-box",
                          zIndex: 10, // Ensure dropdown appears above other elements
                          
                        }}
                      >
                        <List
                          className="listContainer"
                          height={456}
                          itemCount={rows.length}
                          itemSize={getItemSize}
                          width="100%"
                        >
                          {({
                            index,
                            style,
                          }: {
                            index: number;
                            style: React.CSSProperties;
                          }) => (
                            <RowRenderer
                              key={index}
                              index={index}
                              style={style}
                            />
                          )}
                        </List>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </CustomDataTableContainer>
      <CustomPagination
        pageOption={pageOption}
        handleChangePageOption={handleChangePageOption}
        paginationInfo={paginationInfo}
        handlePageChange={handlePageChange}
      />
    </>
  );
};

export default CustomDataTable;
